import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  ChevronDown,
  Clock,
  ExternalLink,
  Mail,
  MapPin,
  Menu,
  Moon,
  Phone,
  Send,
  Sun,
  UserRound,
  X
} from "lucide-react";
import { CSSProperties, FormEvent, ReactNode, StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  courses,
  globalSettings,
  legalPages,
  practiceImages,
  services,
  stats,
  teamMembers,
  tourScenes
} from "./content";
import "pannellum/build/pannellum.css";
import "pannellum/build/pannellum.js";
import "./styles.css";

type Theme = "light" | "dark";
type RevealDirection = "up" | "left" | "right";
type NavGroup = { label: string; href: string } | { label: string; items: Array<{ label: string; href: string }> };

const themeKey = "therabox.theme";
const siteUrl = "https://therabox.schnick-schnack.info";

const navGroups: NavGroup[] = [
  { label: "Home", href: "/" },
  {
    label: "Med. Fitnesstraining",
    items: [
      { label: "Diagnostik", href: "/diagnostics" },
      { label: "Betreuungskonzept", href: "/support" },
      { label: "Training mit System", href: "/training" },
      { label: "Kurse", href: "/kurse" }
    ]
  },
  {
    label: "Über uns",
    items: [
      { label: "Unser Team", href: "/team" },
      { label: "Unsere Praxis", href: "/practice" },
      { label: "Virtueller Rundgang", href: "/virtueller-rundgang" }
    ]
  },
  {
    label: "Physiotherapie",
    items: [
      { label: "Was ist Physiotherapie", href: "/physio" },
      { label: "Unsere Leistungen", href: "/service" }
    ]
  },
  { label: "Kontakt", href: "/contact" }
];

function getPath() {
  return window.location.pathname.replace(/\/$/, "") || "/";
}

function navigate(href: string) {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function usePath() {
  const [path, setPath] = useState(getPath);
  useEffect(() => {
    const onPop = () => setPath(getPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = window.localStorage.getItem(themeKey);
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(themeKey, theme);
  }, [theme]);

  return { theme, setTheme };
}

function LinkButton({ href, children, className = "" }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a
      className={className}
      href={href}
      onClick={(event) => {
        if (href.startsWith("/")) {
          event.preventDefault();
          navigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}

function Reveal({
  children,
  direction = "up",
  delay = 0,
  className = "",
  style
}: {
  children: ReactNode;
  direction?: RevealDirection;
  delay?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (media.matches) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal reveal-${direction} ${visible ? "is-visible" : ""} ${className}`}
      style={{ ...style, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function CountUp({ value }: { value: string }) {
  const target = parseInt(value, 10);
  const suffix = value.replace(String(target), "");
  const ref = useRef<HTMLElement | null>(null);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node || Number.isNaN(target)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target);
      return;
    }
    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const startedAt = performance.now();
        const duration = 1400;
        const tick = (now: number) => {
          const progress = Math.min(1, (now - startedAt) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(target * eased));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target]);

  if (Number.isNaN(target)) return <strong>{value}</strong>;
  return (
    <strong ref={ref}>
      {display}
      {suffix}
    </strong>
  );
}

function BrandLogo({ compact = false }: { compact?: boolean }) {
  return (
    <img
      className={`brand-logo ${compact ? "compact" : ""}`}
      src="/images/logo.png"
      alt="TheraBOX - Physio | Reha | Training"
      width={150}
      height={84}
    />
  );
}

function Header({ theme, toggleTheme }: { theme: Theme; toggleTheme: () => void }) {
  const [open, setOpen] = useState(false);
  const path = usePath();

  useEffect(() => setOpen(false), [path]);

  return (
    <header className="site-header">
      <LinkButton href="/" className="brand">
        <BrandLogo />
      </LinkButton>
      <nav className="desktop-nav" aria-label="Hauptnavigation">
        {navGroups.map((group) =>
          isNavMenu(group) ? (
            <div className="nav-group" key={group.label}>
              <button type="button">
                {group.label} <ChevronDown size={15} />
              </button>
              <div className="nav-panel">
                {group.items.map((item) => (
                  <LinkButton key={item.href} href={item.href} className={path === item.href ? "active" : ""}>
                    {item.label}
                  </LinkButton>
                ))}
              </div>
            </div>
          ) : (
            <LinkButton key={group.href} href={group.href} className={path === group.href ? "active" : ""}>
              {group.label}
            </LinkButton>
          )
        )}
      </nav>
      <div className="header-actions">
        <button className="icon-button" type="button" onClick={toggleTheme} aria-label="Farbschema wechseln">
          {theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}
        </button>
        <button className="icon-button mobile-only" type="button" onClick={() => setOpen(true)} aria-label="Menü öffnen">
          <Menu size={22} />
        </button>
      </div>
      {open ? (
        <div className="mobile-drawer">
          <button className="icon-button drawer-close" type="button" onClick={() => setOpen(false)} aria-label="Menü schließen">
            <X size={22} />
          </button>
          {navGroups.map((group) => (
            <div className="drawer-group" key={group.label}>
              {isNavMenu(group) ? (
                <>
                  <strong>{group.label}</strong>
                  {group.items.map((item) => (
                    <LinkButton key={item.href} href={item.href}>
                      {item.label}
                    </LinkButton>
                  ))}
                </>
              ) : (
                <LinkButton href={group.href}>{group.label}</LinkButton>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </header>
  );
}

function isNavMenu(group: NavGroup): group is Extract<NavGroup, { items: Array<{ label: string; href: string }> }> {
  return "items" in group;
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-copy">
        <Reveal direction="left">
          <p className="eyebrow">Physiotherapie in Sulzbach</p>
          <h1>
            Wir bringen Sie <span className="accent-text">in Bewegung</span>
          </h1>
          <p>
            Moderne Therapie, medizinisches Training und RehaSport in einer Praxis, die Menschen vom ersten Befund bis zur
            Rückkehr in Alltag, Beruf und Sport begleitet.
          </p>
          <div className="button-row">
            <LinkButton href="/contact" className="button primary">
              Anfrage senden <Send size={18} />
            </LinkButton>
            <LinkButton href="/virtueller-rundgang" className="button ghost">
              Rundgang starten <ArrowRight size={18} />
            </LinkButton>
          </div>
          <ul className="hero-trust">
            <li>
              <Clock size={17} /> {globalSettings.openingHours}
            </li>
            <li>
              <a href={`tel:${globalSettings.phone.replace(/\D/g, "")}`}>
                <Phone size={17} /> {globalSettings.phone}
              </a>
            </li>
            <li>
              <a href={globalSettings.mapUrl} target="_blank" rel="noreferrer">
                <MapPin size={17} /> {globalSettings.address}
              </a>
            </li>
          </ul>
        </Reveal>
      </div>
      <Reveal direction="right" className="hero-visual">
        <div className="hero-photo-stack">
          <img className="hero-main-photo" src="/images/practice/behandlungsraum-1.jpg" alt="Behandlungsraum mit Physiotherapieliege bei TheraBOX" />
          <div className="hero-badge">
            <strong>Virtueller Rundgang</strong>
            <span>{tourScenes.length} Stationen durch die Praxis</span>
            <LinkButton href="/virtueller-rundgang" className="text-link">
              Jetzt entdecken <ArrowRight size={16} />
            </LinkButton>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function SectionIntro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <Reveal className="section-intro">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{text}</p>
    </Reveal>
  );
}

function HomePage() {
  return (
    <>
      <Hero />
      <section className="section">
        <SectionIntro
          eyebrow="Willkommen"
          title="Ganzheitliche Begleitung von Therapie bis Training"
          text="TheraBOX verbindet Physiotherapie, medizinisches Fitnesstraining, RehaSport und gesundes Krafttraining zu einem klar geführten Behandlungspfad."
        />
        <div className="stats-grid">
          {stats.map((item, index) => (
            <Reveal key={item.label} delay={index * 90} className="glass-card stat-card">
              <CountUp value={item.value} />
              <span>{item.label}</span>
            </Reveal>
          ))}
        </div>
      </section>
      <ServicesPreview />
      <section className="section split-section">
        <Reveal direction="left" className="glass-card teaser-card">
          <div className="teaser-thumbs" aria-hidden="true">
            {tourScenes.slice(0, 3).map((scene) => (
              <img key={scene.slug} src={scene.image} alt="" loading="lazy" />
            ))}
          </div>
          <h2>Praxis digital erleben</h2>
          <p>Der virtuelle Rundgang führt in {tourScenes.length} Stationen durch Behandlungsraum, Training, Kurse und Faszienbereich.</p>
          <LinkButton href="/virtueller-rundgang" className="button primary">
            Rundgang starten <ArrowRight size={18} />
          </LinkButton>
        </Reveal>
        <Reveal className="glass-card teaser-card" delay={90}>
          <div className="teaser-thumbs" aria-hidden="true">
            <img src="/images/practice/kursraum-1.webp" alt="" loading="lazy" />
            <img src="/images/practice/kursraum-2.jpg" alt="" loading="lazy" />
            <img src="/images/practice/mobilitaet-2.jpg" alt="" loading="lazy" />
          </div>
          <h2>Kurse & RehaSport</h2>
          <p>Alle Kurse der Woche auf einen Blick - als übersichtlicher Wochenplan und als PDF zum Mitnehmen.</p>
          <LinkButton href="/kurse" className="button ghost">
            Kursplan ansehen <CalendarDays size={18} />
          </LinkButton>
        </Reveal>
        <Reveal direction="right" className="glass-card teaser-card" delay={180}>
          <h2>So finden Sie uns</h2>
          <ul className="info-list">
            <li>
              <MapPin size={18} /> {globalSettings.address}
            </li>
            <li>
              <Clock size={18} /> {globalSettings.openingHours}
            </li>
            <li>
              <Phone size={18} /> {globalSettings.phone}
            </li>
          </ul>
          <a className="button ghost" href={globalSettings.mapUrl} target="_blank" rel="noreferrer">
            Route planen <ExternalLink size={18} />
          </a>
        </Reveal>
      </section>
    </>
  );
}

function ServicesPreview() {
  return (
    <section className="section">
      <SectionIntro
        eyebrow="Leistungen"
        title="Was bieten wir an?"
        text="Von Krankengymnastik bis Sportphysiotherapie: zehn Leistungsbereiche aus Therapie, Reha und Training."
      />
      <div className="card-grid">
        {services.map((service, index) => (
          <Reveal key={service.slug} delay={(index % 4) * 80} className="glass-card service-card">
            <img src={service.image} alt={`Fotorealistische Szene: ${service.name}`} />
            <h3>{service.name}</h3>
            <p>{service.shortText}</p>
            <LinkButton href={`/service/${service.id}/${service.slug}`} className="text-link">
              Mehr erfahren <ArrowRight size={16} />
            </LinkButton>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const infoImages: Record<"diagnostics" | "support" | "training" | "physio", { src: string; alt: string }> = {
  diagnostics: {
    src: "/images/info/diagnostik.webp",
    alt: "Physiotherapeut dokumentiert einen Bewegungstest mit Tablet"
  },
  support: {
    src: "/images/info/betreuungskonzept.webp",
    alt: "Beratungsgespräch: Therapeut und Patientin besprechen den Trainingsplan"
  },
  training: {
    src: "/images/info/training-system.webp",
    alt: "Trainingskarte wird in das Terminal eines medizinischen Trainingsgeräts gesteckt"
  },
  physio: {
    src: "/images/info/physiotherapie.webp",
    alt: "Physiotherapeut leitet eine Übung mit dem Übungsband an der Sprossenwand an"
  }
};

function InfoPage({ kind }: { kind: "diagnostics" | "support" | "training" | "physio" }) {
  const data = {
    diagnostics: {
      eyebrow: "Med. Fitnesstraining",
      title: "Diagnostik",
      text: "Im intensiven Austausch mit Physiotherapeutinnen und Physiotherapeuten entsteht ein individuelles Trainings- und Therapiekonzept.",
      points: [
        {
          title: "Gesundheitsprofil",
          text: "Am Anfang steht ein genauer Blick auf Ihre aktuelle Situation: Beschwerden, Belastbarkeit, Alltag, Beruf, Sport, bisherige Diagnosen und persönliche Ziele werden gemeinsam eingeordnet. So entsteht kein Trainingsplan von der Stange, sondern eine Basis, die zu Ihrem Körper, Ihrer Vorgeschichte und Ihrem Zeitrahmen passt."
        },
        {
          title: "Ausgangslage verstehen",
          text: "Beweglichkeit, Kraft, Koordination und funktionelle Bewegungsmuster werden so betrachtet, dass daraus konkrete Trainings- und Therapieentscheidungen entstehen. Wichtig ist nicht nur, wo es schmerzt, sondern welche Strukturen überlastet sind, welche Bewegungen vermieden werden und welche Ressourcen bereits vorhanden sind."
        },
        {
          title: "Ziele definieren",
          text: "Gemeinsam wird festgelegt, was erreicht werden soll: weniger Schmerzen, mehr Stabilität, sichere Alltagsbewegungen, Rückkehr zum Sport oder ein gezielter Kraftaufbau. Diese Ziele werden in überschaubare Schritte übersetzt, damit Fortschritt sichtbar wird und der Plan bei Bedarf angepasst werden kann."
        },
        {
          title: "Beratung anfragen",
          text: "Wenn Sie wissen möchten, ob medizinisches Fitnesstraining, Physiotherapie oder eine Kombination sinnvoll ist, können Sie direkt Kontakt aufnehmen. Das Team klärt mit Ihnen, welche Unterlagen hilfreich sind, ob eine Verordnung benötigt wird und welcher Einstieg für Ihre Situation am besten passt."
        }
      ]
    },
    support: {
      eyebrow: "Betreuungskonzept",
      title: "Drei Schritte zu deinem Trainingsplan",
      text: "Kennenlernen, individueller Trainingsplan und Geräteeinführung strukturieren den Start.",
      points: [
        {
          title: "Vorgespräch",
          text: "Im Vorgespräch geht es um Erwartungen, Beschwerden, Trainingshistorie und praktische Rahmenbedingungen. Wir klären, wie oft Sie trainieren können, welche Bewegungen Ihnen schwerfallen und welche Ziele realistisch erreichbar sind. Dadurch wird der Trainingsstart verständlich, planbar und gut begleitet."
        },
        {
          title: "Gesundheitstests",
          text: "Je nach Ausgangslage werden einfache funktionelle Tests, Bewegungschecks und Belastungseinschätzungen genutzt. Die Ergebnisse helfen dabei, Risiken zu vermeiden, Belastungen sauber zu dosieren und den Trainingsplan so aufzubauen, dass er fordert, aber nicht überfordert."
        },
        {
          title: "Plan auf Basis der Ergebnisse",
          text: "Aus den Testergebnissen entsteht ein individueller Trainingsplan mit Übungen, Geräteeinstellungen, Wiederholungen, Pausen und Progression. Der Plan wird nicht als starres Dokument verstanden, sondern als lebendiges System: Wenn sich Beweglichkeit, Kraft oder Beschwerden verändern, wird angepasst."
        },
        {
          title: "Einführung ins Trainingssystem",
          text: "Zum Start werden Geräte, Bewegungsabläufe und Trainingskarte Schritt für Schritt erklärt. Ziel ist, dass Sie sicher trainieren, die Übungen verstehen und wissen, worauf Sie achten müssen. Bei Fragen oder Unsicherheiten bleibt das Team ansprechbar und begleitet den weiteren Aufbau."
        }
      ]
    },
    training: {
      eyebrow: "Training mit System",
      title: "Plan, Geräteeinstellung und Motivation",
      text: "Sitzhöhen, Bewegungsgeschwindigkeit, Gewichte, Pausen und Wiederholungen werden auf der Trainingskarte gespeichert.",
      points: [
        {
          title: "Individueller Plan",
          text: "Der Trainingsplan orientiert sich an Ihrem Ziel und Ihrer aktuellen Belastbarkeit. Übungen für Kraft, Beweglichkeit, Koordination und Ausdauer werden sinnvoll kombiniert, damit der Körper nicht isoliert, sondern funktionell stärker wird. Die Intensität wird kontrolliert gesteigert."
        },
        {
          title: "Chipkarte",
          text: "Die Trainingskarte speichert wichtige Einstellungen und macht das Training reproduzierbar. Sitzpositionen, Bewegungsumfang, Gewichte und Wiederholungen müssen nicht jedes Mal neu gesucht werden. Das spart Zeit und sorgt dafür, dass jede Einheit möglichst präzise durchgeführt werden kann."
        },
        {
          title: "Vitality-System",
          text: "Das System unterstützt bei Orientierung und Motivation: Übungen werden nachvollziehbar dargestellt, Trainingswerte sind dokumentiert und Fortschritte bleiben sichtbar. Gerade bei regelmäßigem Training hilft diese Struktur, dranzubleiben und Veränderungen objektiver wahrzunehmen."
        },
        {
          title: "Punktestand für Fortschritt",
          text: "Fortschritt entsteht nicht nur durch höhere Gewichte. Auch bessere Kontrolle, sauberere Bewegungen, weniger Beschwerden oder mehr Sicherheit im Alltag zählen. Der Punktestand und die dokumentierten Trainingsdaten helfen dabei, Entwicklung greifbar zu machen und neue Ziele abzuleiten."
        }
      ]
    },
    physio: {
      eyebrow: "Physiotherapie",
      title: "Was ist Physiotherapie?",
      text: "Physiotherapie behandelt Haltung, Bewegung, innere Organe und Nervensystem mit dem Ziel, Alltag und Schmerzfreiheit zu verbessern.",
      points: [
        {
          title: "Erkrankungen",
          text: "Physiotherapie kann bei akuten und chronischen Beschwerden helfen, wenn Bewegung, Kraft, Koordination oder Belastbarkeit eingeschränkt sind. Dazu gehören unter anderem Rücken- und Gelenkbeschwerden, neurologische Erkrankungen, postoperative Zustände oder Einschränkungen nach längerer Schonung."
        },
        {
          title: "Fehlentwicklungen",
          text: "Wenn Bewegungsmuster ungünstig werden, entstehen oft Ausweichbewegungen, Überlastungen oder Unsicherheit. Physiotherapie setzt hier an: Bewegungen werden analysiert, gezielt korrigiert und so aufgebaut, dass sie im Alltag wieder zuverlässig funktionieren."
        },
        {
          title: "Verletzungsfolgen",
          text: "Nach Verletzungen oder Operationen geht es darum, Beweglichkeit, Stabilität und Vertrauen schrittweise zurückzugewinnen. Die Behandlung wird an Heilungsphase, ärztliche Vorgaben und persönliche Ziele angepasst, damit Belastung sinnvoll gesteigert werden kann."
        },
        {
          title: "Funktionsstörungen",
          text: "Nicht jede Einschränkung ist direkt auf einem Bild oder Befund sichtbar. Schmerzen, Blockadegefühle, muskuläre Dysbalancen, Koordinationsprobleme oder eingeschränkte Beweglichkeit können funktionell zusammenhängen. Physiotherapie sucht diese Zusammenhänge und behandelt sie nachvollziehbar."
        }
      ]
    }
  }[kind];

  return (
    <section className="page-section">
      <SectionIntro eyebrow={data.eyebrow} title={data.title} text={data.text} />
      <Reveal className="info-visual">
        <img src={infoImages[kind].src} alt={infoImages[kind].alt} loading="lazy" />
      </Reveal>
      <div className="timeline">
        {data.points.map((point, index) => (
          <Reveal key={point.title} direction={index % 2 ? "right" : "left"} className="glass-card timeline-item">
            <span>{index + 1}</span>
            <div className="timeline-content">
              <h3>{point.title}</h3>
              <p>{point.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="center-actions">
        <LinkButton href="/contact" className="button primary">
          Beratung anfragen <Send size={18} />
        </LinkButton>
      </Reveal>
    </section>
  );
}

function CoursesPage() {
  const [filter, setFilter] = useState<"Alle" | CourseFilter>("Alle");
  type CourseFilter = "RehaSport" | "Mitgliederkurs";
  const filtered = filter === "Alle" ? courses : courses.filter((course) => course.type === filter);
  const weekdays = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag"];

  return (
    <section className="page-section">
      <SectionIntro
        eyebrow="Kurse"
        title="Kursplan"
        text="Alle RehaSport- und Mitgliederkurse der Woche im Überblick - auch als PDF zum Mitnehmen."
      />
      <Reveal className="filter-row">
        {(["Alle", "RehaSport", "Mitgliederkurs"] as const).map((item) => (
          <button key={item} className={filter === item ? "active" : ""} type="button" onClick={() => setFilter(item)}>
            {item}
          </button>
        ))}
        <a className="button ghost" href="/downloads/kursplan.pdf" target="_blank" rel="noreferrer">
          PDF öffnen <ExternalLink size={18} />
        </a>
      </Reveal>
      <div className="week-grid">
        {weekdays.map((day, dayIndex) => {
          const dayCourses = filtered.filter((course) => course.weekday === day);
          return (
            <Reveal key={day} delay={dayIndex * 60} className="week-day">
              <h3>{day}</h3>
              {dayCourses.length === 0 ? <p className="week-empty">Keine Kurse</p> : null}
              {dayCourses.map((course) => (
                <div
                  key={`${course.time}-${course.title}`}
                  className={`glass-card course-card ${course.type === "RehaSport" ? "is-reha" : "is-member"}`}
                >
                  <strong>{course.time}</strong>
                  <h4>{course.title}</h4>
                  <span>{course.condition}</span>
                </div>
              ))}
            </Reveal>
          );
        })}
      </div>
      <Reveal className="notice glass-card">
        <p>Anmeldung zur Teilnahme telefonisch unter {globalSettings.coursePhone}. Öffnungszeiten: {globalSettings.openingHours}.</p>
      </Reveal>
    </section>
  );
}

function TeamPage() {
  return (
    <section className="page-section">
      <SectionIntro
        eyebrow="Team"
        title="Die Menschen hinter der Praxis"
        text="Physiotherapie, Training und Organisation: Das sind die Menschen, die Sie bei TheraBOX begleiten."
      />
      <div className="team-scroll">
        {teamMembers.map((member, index) => (
          <article className={`team-feature ${index % 2 ? "reverse" : ""}`} key={member.slug}>
            <Reveal
              direction={index % 2 ? "right" : "left"}
              className="glass-card team-copy"
              style={{ "--member-color": member.accent } as CSSProperties}
            >
              <div className="team-card-image">
                <img src={member.portrait} alt={`Portrait von ${member.name}`} />
              </div>
              <div className="team-card-content">
                <p className="eyebrow">{member.role}</p>
                <h2>{member.name}</h2>
                <p>{member.shortBio}</p>
                <div className="chip-row">
                  {member.qualifications.slice(0, 4).map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
                <LinkButton href={`/team/${member.id}/${member.slug}`} className="button ghost">
                  Profil ansehen <UserRound size={18} />
                </LinkButton>
              </div>
            </Reveal>
          </article>
        ))}
      </div>
    </section>
  );
}

function TeamDetail({ slug }: { slug: string }) {
  const member = teamMembers.find((item) => item.slug === slug);
  if (!member) return <NotFoundPage />;
  return (
    <DetailLayout
      eyebrow={member.role}
      title={member.name}
      image={member.portrait}
      backHref="/team"
      backLabel="Zur Teamübersicht"
      paragraphs={member.detailBio}
      chips={member.qualifications}
      accent={member.accent}
    />
  );
}

function ServicesPage() {
  const [selected, setSelected] = useState(services[0]);
  return (
    <section className="page-section">
      <SectionIntro
        eyebrow="Leistungen"
        title="Eine Liege, zehn Behandlungsszenen"
        text="Wählen Sie eine Leistung aus und sehen Sie direkt, wie die Behandlung aussieht."
      />
      <div className="service-stage-layout">
        <Reveal direction="left" className="glass-card service-picker">
          {services.map((service) => (
            <button key={service.slug} className={selected.slug === service.slug ? "active" : ""} type="button" onClick={() => setSelected(service)}>
              <span>{service.id}</span>
              {service.name}
            </button>
          ))}
        </Reveal>
        <Reveal direction="right" className="glass-card service-stage-card">
          <img className="service-stage-photo" src={selected.image} alt={`Fotorealistische Behandlungsszene: ${selected.name}`} />
          <p className="eyebrow">{selected.tags.join(" / ")}</p>
          <h2>{selected.name}</h2>
          <p>{selected.shortText}</p>
          <LinkButton href={`/service/${selected.id}/${selected.slug}`} className="button primary">
            Detail ansehen <ArrowRight size={18} />
          </LinkButton>
        </Reveal>
      </div>
      <ServicesPreview />
    </section>
  );
}

function ServiceDetail({ slug }: { slug: string }) {
  const service = services.find((item) => item.slug === slug);
  if (!service) return <NotFoundPage />;
  return (
    <section className="page-section detail-layout">
      <Reveal direction="left" className="detail-image service-detail-scene">
        <img src={service.image} alt={`Fotorealistische Behandlungsszene: ${service.name}`} />
      </Reveal>
      <Reveal direction="right" className="glass-card detail-copy">
        <LinkButton href="/service" className="text-link">
          <ArrowLeft size={16} /> Zur Leistungsübersicht
        </LinkButton>
        <p className="eyebrow">Leistung</p>
        <h1>{service.name}</h1>
        {service.detailText.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <div className="chip-row">
          {service.tags.map((chip) => (
            <span key={chip}>{chip}</span>
          ))}
        </div>
        <LinkButton href="/contact" className="button primary">
          Kontakt aufnehmen <Send size={18} />
        </LinkButton>
      </Reveal>
    </section>
  );
}

function DetailLayout({
  eyebrow,
  title,
  image,
  backHref,
  backLabel,
  paragraphs,
  chips,
  accent
}: {
  eyebrow: string;
  title: string;
  image: string;
  backHref: string;
  backLabel: string;
  paragraphs: string[];
  chips: string[];
  accent?: string;
}) {
  return (
    <section className="page-section detail-layout" style={accent ? ({ "--member-color": accent } as CSSProperties) : undefined}>
      <Reveal direction="left" className="detail-image">
        <img src={image} alt="" />
      </Reveal>
      <Reveal direction="right" className="glass-card detail-copy">
        <LinkButton href={backHref} className="text-link">
          <ArrowLeft size={16} /> {backLabel}
        </LinkButton>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <div className="chip-row">
          {chips.map((chip) => (
            <span key={chip}>{chip}</span>
          ))}
        </div>
        <LinkButton href="/contact" className="button primary">
          Kontakt aufnehmen <Send size={18} />
        </LinkButton>
      </Reveal>
    </section>
  );
}

function PracticePage() {
  const [active, setActive] = useState<string | null>(null);
  return (
    <section className="page-section">
      <SectionIntro
        eyebrow="Praxis"
        title="Räume für Therapie, Training und RehaSport"
        text="Ein ehrlicher Blick in unsere Räume - jedes Foto ist zugleich eine Station des virtuellen Rundgangs."
      />
      <div className="gallery-grid">
        {practiceImages.map((image, index) => (
          <Reveal key={image.tour} delay={(index % 4) * 70} className="gallery-item" direction={index % 2 ? "right" : "left"}>
            <button type="button" onClick={() => setActive(image.image)} aria-label={`${image.title} vergrößern`}>
              <img src={image.image} alt={image.title} loading="lazy" />
            </button>
            <div className="gallery-caption">
              <span>{image.title}</span>
              <LinkButton href={`/virtueller-rundgang#${image.tour}`} className="gallery-tour-link">
                Im Rundgang <ArrowRight size={14} />
              </LinkButton>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="center-actions">
        <LinkButton href="/virtueller-rundgang" className="button primary">
          Rundgang starten <ArrowRight size={18} />
        </LinkButton>
      </Reveal>
      {active ? (
        <div className="lightbox" onClick={() => setActive(null)} role="dialog" aria-modal="true">
          <button className="icon-button" type="button" aria-label="Lightbox schließen">
            <X size={22} />
          </button>
          <img src={active} alt="Vergrößerte Praxisansicht" />
        </div>
      ) : null}
    </section>
  );
}

function TourPage() {
  const total = tourScenes.length;
  const initialIndex = () => tourScenes.findIndex((item) => `#${item.slug}` === window.location.hash);
  const [started, setStarted] = useState(() => initialIndex() >= 0);
  const [current, setCurrent] = useState(() => Math.max(0, initialIndex()));
  const stageRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<PannellumViewer | null>(null);
  const scene = tourScenes[current];

  useEffect(() => {
    if (!started || !stageRef.current || viewerRef.current) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scenes = Object.fromEntries(
      tourScenes.map((item) => [
        item.slug,
        {
          type: "equirectangular",
          panorama: item.pano,
          preview: item.panoPreview,
          yaw: item.yaw,
          pitch: item.pitch ?? 0,
          hfov: 100,
          hotSpots: item.links.map((link) => {
            const target = tourScenes.find((candidate) => candidate.slug === link.target);
            return {
              yaw: link.yaw,
              pitch: -1,
              type: "scene",
              sceneId: link.target,
              text: target ? target.title : link.target,
              cssClass: "hotspot-360"
            };
          })
        }
      ])
    );
    const viewer = window.pannellum.viewer(stageRef.current, {
      default: {
        firstScene: tourScenes[Math.max(0, initialIndex())]?.slug ?? tourScenes[0].slug,
        sceneFadeDuration: 900,
        autoLoad: true,
        showControls: false,
        compass: false,
        autoRotate: reducedMotion ? 0 : -2,
        autoRotateInactivityDelay: 8000,
        minHfov: 55,
        maxHfov: 110,
        friction: 0.15
      },
      scenes
    });
    viewer.on("scenechange", (...args: unknown[]) => {
      const slug = args[0] as string;
      const index = tourScenes.findIndex((item) => item.slug === slug);
      if (index >= 0) setCurrent(index);
    });
    viewerRef.current = viewer;
    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  useEffect(() => {
    if (started) window.history.replaceState({}, "", `/virtueller-rundgang#${scene.slug}`);
  }, [started, scene.slug]);

  useEffect(() => {
    const onHashChange = () => {
      const index = initialIndex();
      if (index < 0) return;
      setStarted(true);
      const viewer = viewerRef.current;
      if (viewer && viewer.getScene() !== tourScenes[index].slug) viewer.loadScene(tourScenes[index].slug);
      setCurrent(index);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function goTo(index: number) {
    if (index < 0 || index >= total || index === current) return;
    const viewer = viewerRef.current;
    if (viewer && viewer.getScene() !== tourScenes[index].slug) {
      viewer.loadScene(tourScenes[index].slug);
    }
    setCurrent(index);
  }

  return (
    <section className="page-section">
      <SectionIntro
        eyebrow="Virtueller Rundgang"
        title="Die Praxis in 360° erleben"
        text="Sehen Sie sich in jedem Raum frei um - per Maus, Touch oder Pfeiltasten - und wechseln Sie über die Türen in den nächsten Raum."
      />
      {!started ? (
        <Reveal className="tour-start glass-card">
          <div className="tour-start-thumbs" aria-hidden="true">
            {tourScenes.slice(0, 4).map((item) => (
              <img key={item.slug} src={item.image} alt="" loading="lazy" />
            ))}
          </div>
          <h2>Rundgang starten</h2>
          <p>
            {total} Räume in echtem 360° - vom Empfang bis zum Faszienraum. Umsehen mit Maus, Fingern oder Pfeiltasten, Räume
            wechseln über die markierten Türen.
          </p>
          <button className="button primary" type="button" onClick={() => setStarted(true)}>
            Rundgang starten <ArrowRight size={18} />
          </button>
        </Reveal>
      ) : (
        <div className="tour-layout">
          <div className="tour-stage-column">
            <Reveal direction="left" className="tour-stage-frame">
              <div className="tour-stage">
                <div ref={stageRef} className="tour-pano" aria-label={`360-Grad-Ansicht: ${scene.title}`} />
                <div className="tour-progress" aria-hidden="true">
                  <span style={{ width: `${((current + 1) / total) * 100}%` }} />
                </div>
              </div>
            </Reveal>
            <p className="tour-hint">Zum Umsehen ziehen oder Pfeiltasten nutzen · Shift/Strg zoomt · grüne Punkte wechseln den Raum</p>
            <div className="tour-controls">
              <button className="button ghost" type="button" disabled={current === 0} onClick={() => goTo(current - 1)}>
                <ArrowLeft size={18} /> Zurück
              </button>
              <span className="tour-count">
                Station {current + 1} von {total}
              </span>
              {current < total - 1 ? (
                <button className="button primary" type="button" onClick={() => goTo(current + 1)}>
                  Weiter <ArrowRight size={18} />
                </button>
              ) : (
                <LinkButton href="/contact" className="button primary">
                  Termin anfragen <Send size={18} />
                </LinkButton>
              )}
            </div>
          </div>
          <Reveal direction="right" className="glass-card tour-side">
            <p className="eyebrow">{scene.roomType}</p>
            <h2>{scene.title}</h2>
            <p>{scene.description}</p>
            <div className="scene-thumbs">
              {tourScenes.map((item, index) => (
                <button
                  key={item.slug}
                  className={index === current ? "active" : ""}
                  type="button"
                  onClick={() => goTo(index)}
                  aria-label={`Zu Station ${index + 1}: ${item.title}`}
                >
                  <img src={item.image} alt="" loading="lazy" />
                  <span>{item.title}</span>
                </button>
              ))}
            </div>
            {current === total - 1 ? (
              <div className="tour-finale">
                <strong>Das war der Rundgang.</strong>
                <p>Lernen Sie die Praxis persönlich kennen - wir freuen uns auf Sie.</p>
                <div className="button-row">
                  <LinkButton href="/contact" className="button primary">
                    Termin anfragen <Send size={18} />
                  </LinkButton>
                  <LinkButton href="/kurse" className="button ghost">
                    Kursplan ansehen <CalendarDays size={18} />
                  </LinkButton>
                </div>
              </div>
            ) : null}
          </Reveal>
        </div>
      )}
    </section>
  );
}

function ContactPage() {
  const [state, setState] = useState<{ status: "idle" | "sending" | "success" | "error"; message?: string }>({
    status: "idle"
  });
  type Captcha = { mode: "rotate" | "question"; token: string; image?: string; question?: string };
  const [captcha, setCaptcha] = useState<Captcha | null>(null);
  const [captchaAngle, setCaptchaAngle] = useState(0);

  async function loadCaptcha(mode: "rotate" | "question" = "rotate") {
    setCaptcha(null);
    setCaptchaAngle(0);
    const response = await fetch(`/api/captcha?mode=${mode}`, { cache: "no-store" });
    if (!response.ok) throw new Error("captcha_failed");
    setCaptcha((await response.json()) as Captcha);
  }

  useEffect(() => {
    loadCaptcha().catch(() => setCaptcha(null));
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ status: "sending" });
    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = (await response.json().catch(() => null)) as { message?: string } | null;
      if (response.ok) {
        setState({ status: "success", message: data?.message ?? "Vielen Dank für Ihre Nachricht. Wir melden uns zeitnah zurück." });
        form.reset();
      } else {
        setState({
          status: "error",
          message: data?.message ?? "Die Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut."
        });
      }
      await loadCaptcha(captcha?.mode ?? "rotate");
    } catch {
      setState({
        status: "error",
        message: "Die Nachricht konnte nicht gesendet werden. Bitte rufen Sie uns an oder versuchen Sie es später erneut."
      });
      loadCaptcha(captcha?.mode ?? "rotate").catch(() => setCaptcha(null));
    }
  }

  return (
    <section className="page-section contact-layout">
      <Reveal direction="left" className="glass-card contact-cards">
        <p className="eyebrow">Kontakt</p>
        <h1>Wir melden uns zurück</h1>
        <a href={`tel:${globalSettings.phone.replace(/\D/g, "")}`}>
          <Phone size={19} /> {globalSettings.phone}
        </a>
        <a href={`mailto:${globalSettings.email}`}>
          <Mail size={19} /> {globalSettings.email}
        </a>
        <a href={globalSettings.mapUrl} target="_blank" rel="noreferrer">
          <MapPin size={19} /> {globalSettings.address}
        </a>
        <p className="contact-hours">
          <Clock size={19} /> {globalSettings.openingHours}
        </p>
        <div className="contact-photo">
          <img src="/images/practice/behandlungsraum-1.jpg" alt="Behandlungsraum der TheraBOX" loading="lazy" />
        </div>
      </Reveal>
      <Reveal direction="right" className="glass-card form-card">
        <form onSubmit={submit}>
          <label>
            Name
            <input name="name" required autoComplete="name" />
          </label>
          <label>
            E-Mail
            <input name="email" type="email" required autoComplete="email" />
          </label>
          <label>
            Telefon optional
            <input name="phone" autoComplete="tel" />
          </label>
          <label>
            Betreff
            <input name="subject" required />
          </label>
          <label className="full">
            Nachricht
            <textarea name="message" required rows={6} />
          </label>
          <input className="honeypot" name="company" tabIndex={-1} autoComplete="off" />
          <input name="captchaToken" type="hidden" value={captcha?.token ?? ""} readOnly />
          <div className="captcha-box full">
            <strong className="captcha-title">Sicherheitsprüfung</strong>
            {captcha === null ? (
              <p className="captcha-hint">Sicherheitsprüfung wird geladen ...</p>
            ) : captcha.mode === "question" ? (
              <label className="captcha-question">
                {captcha.question}
                <input name="captchaAnswer" required autoComplete="off" />
              </label>
            ) : (
              <>
                <p className="captcha-hint">Drehen Sie das Foto mit dem Regler, bis es richtig herum steht.</p>
                <div className="captcha-rotate">
                  <img
                    src={captcha.image}
                    alt="Sicherheitsbild: ein verdrehtes Foto aus der Praxis"
                    style={{ transform: `rotate(${captchaAngle}deg)` }}
                  />
                  <input
                    type="range"
                    min={0}
                    max={359}
                    step={1}
                    value={captchaAngle}
                    onChange={(event) => setCaptchaAngle(Number(event.target.value))}
                    aria-label="Bild drehen (in Grad)"
                  />
                </div>
                <input name="captchaAnswer" type="hidden" value={captchaAngle} readOnly />
              </>
            )}
            <button
              className="captcha-alt"
              type="button"
              onClick={() => loadCaptcha(captcha?.mode === "question" ? "rotate" : "question").catch(() => setCaptcha(null))}
            >
              {captcha?.mode === "question" ? "Lieber das Bild drehen" : "Alternative ohne Bild (barrierefrei)"}
            </button>
          </div>
          <label className="checkbox full">
            <input name="privacy" type="checkbox" required /> Ich stimme der Verarbeitung meiner Angaben zur Kontaktaufnahme zu.
          </label>
          <button className="button primary" type="submit" disabled={state.status === "sending"}>
            {state.status === "sending" ? "Wird gesendet..." : "Absenden"} <Send size={18} />
          </button>
          {state.status === "success" || state.status === "error" ? (
            <p className={`form-status ${state.status}`} role="status" aria-live="polite">
              {state.message}
            </p>
          ) : null}
        </form>
      </Reveal>
    </section>
  );
}

function LegalPage({ page }: { page: keyof typeof legalPages }) {
  const data = legalPages[page];
  return (
    <section className="page-section narrow">
      <Reveal className="glass-card legal-card">
        <h1>{data.title}</h1>
        {data.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </Reveal>
    </section>
  );
}

function NotFoundPage() {
  return (
    <section className="page-section narrow">
      <Reveal className="glass-card legal-card">
        <h1>Seite nicht gefunden</h1>
        <p>Diese Route existiert nicht. Die wichtigsten Bereiche sind direkt erreichbar.</p>
        <div className="button-row">
          <LinkButton href="/" className="button primary">
            Home
          </LinkButton>
          <LinkButton href="/service" className="button ghost">
            Leistungen
          </LinkButton>
          <LinkButton href="/contact" className="button ghost">
            Kontakt
          </LinkButton>
        </div>
      </Reveal>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div>
        <BrandLogo compact />
      </div>
      <div>
        <LinkButton href="/service">Leistungen</LinkButton>
        <LinkButton href="/team">Team</LinkButton>
        <LinkButton href="/virtueller-rundgang">Rundgang</LinkButton>
      </div>
      <div>
        <LinkButton href="/privacy">Datenschutz</LinkButton>
        <LinkButton href="/disclaimer">Haftungsausschluss</LinkButton>
        <LinkButton href="/impressum">Impressum</LinkButton>
      </div>
      <div>
        <p>{globalSettings.address}</p>
        <a href={`tel:${globalSettings.phone.replace(/\D/g, "")}`}>{globalSettings.phone}</a>
        <a href={`mailto:${globalSettings.email}`}>{globalSettings.email}</a>
        <p>{globalSettings.openingHours}</p>
      </div>
    </footer>
  );
}

function App() {
  const path = usePath();
  const { theme, setTheme } = useTheme();
  const page = useMemo(() => {
    const serviceMatch = path.match(/^\/service\/\d+\/([^/]+)$/);
    const teamMatch = path.match(/^\/team\/\d+\/([^/]+)$/);
    if (serviceMatch) return <ServiceDetail slug={serviceMatch[1]} />;
    if (teamMatch) return <TeamDetail slug={teamMatch[1]} />;
    switch (path) {
      case "/":
        return <HomePage />;
      case "/diagnostics":
        return <InfoPage kind="diagnostics" />;
      case "/support":
        return <InfoPage kind="support" />;
      case "/training":
        return <InfoPage kind="training" />;
      case "/kurse":
        return <CoursesPage />;
      case "/team":
        return <TeamPage />;
      case "/practice":
        return <PracticePage />;
      case "/physio":
        return <InfoPage kind="physio" />;
      case "/service":
        return <ServicesPage />;
      case "/contact":
        return <ContactPage />;
      case "/privacy":
        return <LegalPage page="privacy" />;
      case "/disclaimer":
        return <LegalPage page="disclaimer" />;
      case "/impressum":
        return <LegalPage page="impressum" />;
      case "/virtueller-rundgang":
        return <TourPage />;
      default:
        return <NotFoundPage />;
    }
  }, [path]);

  useEffect(() => {
    const meta = getRouteMeta(path);
    document.title = meta.title;
    setMeta("description", meta.description);
    setCanonical(`${siteUrl}${path}`);
    setLocalBusinessJsonLd();
  }, [path]);

  return (
    <>
      <Header theme={theme} toggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")} />
      <main>{page}</main>
      <Footer />
    </>
  );
}

function getRouteMeta(path: string) {
  const serviceMatch = path.match(/^\/service\/\d+\/([^/]+)$/);
  const teamMatch = path.match(/^\/team\/\d+\/([^/]+)$/);
  const service = serviceMatch ? services.find((item) => item.slug === serviceMatch[1]) : null;
  const member = teamMatch ? teamMembers.find((item) => item.slug === teamMatch[1]) : null;
  if (service) {
    return {
      title: `${service.name} | TheraBOX Sulzbach`,
      description: service.shortText
    };
  }
  if (member) {
    return {
      title: `${member.name} | TheraBOX Team`,
      description: `${member.name}, ${member.role}: ${member.shortBio}`
    };
  }
  const map: Record<string, { title: string; description: string }> = {
    "/": {
      title: "TheraBOX - Physio | Reha | Training in Sulzbach",
      description: "TheraBOX in Sulzbach verbindet Physiotherapie, RehaSport, medizinisches Training und gesundes Krafttraining."
    },
    "/diagnostics": {
      title: "Diagnostik | TheraBOX",
      description: "Diagnostik und Beratung als Basis für individuelle Trainings- und Therapiekonzepte bei TheraBOX."
    },
    "/support": {
      title: "Betreuungskonzept | TheraBOX",
      description: "Kennenlernen, Gesundheitsprofil, Trainingsplan und Geräteeinführung als strukturierter Start ins Training."
    },
    "/training": {
      title: "Training mit System | TheraBOX",
      description: "Medizinisches Training mit individuellem Plan, Chipkarte, Geräteeinstellung und Fortschrittsmotivation."
    },
    "/kurse": {
      title: "Kurse und RehaSport | TheraBOX",
      description: "Aktueller Kursplan für RehaSport und Mitgliederkurse bei TheraBOX in Sulzbach."
    },
    "/team": {
      title: "Team | TheraBOX",
      description: "Das TheraBOX-Team aus Physiotherapie, Training und Büromanagement."
    },
    "/practice": {
      title: "Praxis | TheraBOX",
      description: "Einblick in Behandlungsräume, Kursbereich und medizinisches Training bei TheraBOX."
    },
    "/physio": {
      title: "Physiotherapie | TheraBOX",
      description: "Physiotherapie bei TheraBOX zur Behandlung von Haltung, Bewegung, Alltagseinschränkungen und Schmerzen."
    },
    "/service": {
      title: "Leistungen | TheraBOX",
      description: "Überblick über Physiotherapie, Manuelle Therapie, KGG, Lymphdrainage, Sportphysiotherapie und weitere Leistungen."
    },
    "/contact": {
      title: "Kontakt | TheraBOX",
      description: "Kontakt zu TheraBOX in Sulzbach: Telefon, E-Mail, Adresse und Kontaktformular."
    },
    "/privacy": {
      title: "Datenschutz | TheraBOX",
      description: "Datenschutzhinweise für die Website und das Kontaktformular von TheraBOX."
    },
    "/disclaimer": {
      title: "Haftungsausschluss | TheraBOX",
      description: "Haftungsausschluss und Hinweise zu medizinischen Informationen auf der TheraBOX-Website."
    },
    "/impressum": {
      title: "Impressum | TheraBOX",
      description: "Impressum und Anbieterkennzeichnung der TheraBOX-Website."
    },
    "/virtueller-rundgang": {
      title: "Virtueller Rundgang | TheraBOX",
      description: "Foto-basierter virtueller Rundgang durch Praxis, Behandlungsräume, Kursbereich und Training bei TheraBOX."
    }
  };
  return map[path] ?? { title: "Seite nicht gefunden | TheraBOX", description: "Die gewünschte TheraBOX-Seite wurde nicht gefunden." };
}

function setMeta(name: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(href: string) {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
}

function setLocalBusinessJsonLd() {
  const id = "therabox-local-business-jsonld";
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Physiotherapy",
    name: `${globalSettings.practiceName} - ${globalSettings.claim}`,
    url: `${siteUrl}/`,
    email: globalSettings.email,
    telephone: globalSettings.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Lazarettstraße 3-5",
      postalCode: "66280",
      addressLocality: "Sulzbach",
      addressCountry: "DE"
    },
    openingHours: "Mo-Fr 08:00-18:00"
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
